  (function () {
    const root = document.querySelector('[data-js="search"]');
    if (!root || root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';

    const grid = root.querySelector('[data-js="search-grid"]');
    if (!grid) return;

    const skeletonGrid = root.querySelector('[data-js="search-skeleton"]');
    const template = root.querySelector('[data-js="search-card-template"]');
    const paginationWrapper = root.querySelector('[data-js="search-pagination"]');
    const progressEl = paginationWrapper?.querySelector('[data-js="pagination-progress"]');
    const loadMoreBtn = paginationWrapper?.querySelector('[data-js="pagination-load-more"]');
    const loadMoreText = paginationWrapper?.querySelector('[data-js="pagination-load-more-text"]');
    const spinnerEl = paginationWrapper?.querySelector('[data-js="pagination-spinner"]');
    const navEl = paginationWrapper?.querySelector('[data-js="pagination-nav"]');
    const resultsCountEl = document.querySelector('[data-js="search-results-count"]');
    const toggleBtn = document.querySelector('[data-js="product-display-toggle"]');
    const noResultsEl = root.querySelector('[data-js="search-no-results"]');
    const noResultsMessageEl = noResultsEl?.querySelector('.search-no-results__message');
    const recommendationsEl = document.querySelector('[data-js="search-page-recommendations"]');
    const recommendationsListEl = recommendationsEl?.querySelector('[data-js="search-page-recommendations-list"]');

    const i18n = {
      progress: paginationWrapper?.dataset.i18nProgress || "You've viewed [[end]] of [[total]] results",
      allLoaded: paginationWrapper?.dataset.i18nAllLoaded || 'All [[total]] results loaded',
      loadMore: paginationWrapper?.dataset.i18nLoadMore || 'Load more',
      previous: paginationWrapper?.dataset.i18nPrevious || 'Previous',
      next: paginationWrapper?.dataset.i18nNext || 'Next',
      page: paginationWrapper?.dataset.i18nPage || 'Go to page [[page]]',
    };

    const currencyFormat = root.dataset.currencyFormat || '';
    const basePath = window.location.pathname;
    const query = root.dataset.searchTerms || '';

    const state = {
      lowestPage: 1,
      highestPage: 1,
      activePage: 1,
      totalPages: 0,
      totalResults: 0,
      perPage: 24,
      displayedCount: 0,
      isFetching: false,
      pendingLoad: null,
    };

    const cardDataMap = new WeakMap();
    const activeObservers = [];
    let initialGridRender = true;

    /**
     * Register an observer for cleanup on the next grid re-render.
     *
     * @param {IntersectionObserver} observer - Observer to track.
     * @returns {IntersectionObserver} The same observer.
     */
    function trackObserver(observer) {
      activeObservers.push(observer);
      return observer;
    }

    /** Disconnect and forget all tracked observers. */
    function disconnectObservers() {
      activeObservers.forEach(function (observer) {
        observer.disconnect();
      });
      activeObservers.length = 0;
    }

    /**
     * Reveals the native Shopify search results as a fallback.
     */
    const revealFallback = () => {
      grid.removeAttribute('hidden');
      if (skeletonGrid) skeletonGrid.setAttribute('hidden', '');
      if (paginationWrapper) paginationWrapper.hidden = true;

      if (resultsCountEl) {
        const fallbackCount = parseInt(resultsCountEl.dataset.fallbackCount, 10) || 0;
        updateResultsCount(fallbackCount);
      }

      lazyLoadCards(grid);
    };

    if (!query) {
      if (skeletonGrid) skeletonGrid.setAttribute('hidden', '');
      return;
    }

    if (!window.AthosAPI || !template) return revealFallback();

    /**
     * Lazy-load `<img data-src>` elements as their card scrolls into view.
     * @param {ParentNode} scope
     */
    const lazyLoadCards = (scope) => {
      const targets = [];
      scope.querySelectorAll('media-slider').forEach((slider) => {
        const active =
          slider.querySelector('.media-slider__slide[aria-current="true"]') ||
          slider.querySelector('.media-slider__slide');
        if (!active) return;
        const img = active.querySelector('img[data-src]');
        if (img) targets.push(img);
      });
      if (targets.length === 0) return;

      const swap = (img) => {
        const src = img.dataset.src;
        if (!src) return;
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          delete img.dataset.srcset;
        }
        img.src = src;
        delete img.dataset.src;
        if (typeof img.decode === 'function') img.decode().catch(() => {});
      };

      if (typeof IntersectionObserver !== 'function') {
        targets.forEach(swap);
        return;
      }

      const observer = trackObserver(
        new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              swap(entry.target);
              obs.unobserve(entry.target);
            });
          },
          { rootMargin: '200px 0px' }
        )
      );
      targets.forEach((img) => observer.observe(img));
    };

    /**
     * Immediately hydrate the leading cards' primary images with eager,
     * high-priority loading so the likely LCP image skips the
     * IntersectionObserver queue. Idempotent: promoted images lose their
     * data-src and are ignored on any later pass.
     * @param {Element[]} cards - Rendered cards in display order.
     * @param {number} count - How many leading cards to prioritise.
     */
    const prioritiseLeadImages = (cards, count) => {
      cards.slice(0, count).forEach((card) => {
        const active =
          card.querySelector('.media-slider__slide[aria-current="true"]') || card.querySelector('.media-slider__slide');
        const img = active ? active.querySelector('img[data-src]') : null;
        if (!img) return;
        img.setAttribute('loading', 'eager');
        img.setAttribute('fetchpriority', 'high');
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          delete img.dataset.srcset;
        }
        img.src = img.dataset.src;
        delete img.dataset.src;
        if (typeof img.decode === 'function') img.decode().catch(() => {});
      });
    };

    /**
     * Observe rendered cards with IntersectionObserver and fire impression
     * beacons for items that enter the viewport.
     */
    const observeImpressions = (responseId, results, cards) => {
      if (!window.AthosBeacon || !responseId) return;
      if (!('IntersectionObserver' in window)) {
        window.AthosBeacon.sendSearchImpression(responseId, results);
        return;
      }
      const seen = new Set();
      const observer = trackObserver(
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const card = entry.target;
              const index = cards.indexOf(card);
              if (index === -1 || seen.has(index)) return;
              seen.add(index);
              observer.unobserve(card);
              if (results[index]) {
                window.AthosBeacon.sendSearchImpression(responseId, [results[index]]);
              }
            });
          },
          { threshold: 0.5 }
        )
      );
      cards.forEach((card) => observer.observe(card));
    };

    /**
     * Map a Searchspring/Athos result onto the product-card-template bindings.
     * @param {object} r
     * @returns {object}
     */
    const normalise = (r) => {
      const {
        price: rawPrice,
        msrp,
        name,
        handle,
        url,
        ss_images,
        imageUrl,
        thumbnailImageUrl,
        ss_sizes,
        ss_badge_text,
        ss_badge_bg,
        ss_badge_colour,
        ss_tags,
        attributes = {},
        uid,
        id,
        mappings,
        mfield_custom_urgency_banners,
        custom_urgency_banners,
        urgency_banners,
        mfield_custom_product_label,
        mfield_yotpo_preloaded_bottomline,
      } = r;

      // Athos results carry data under either the flat legacy shape or
      // mappings.core (newer shape) — read both, flat as the fallback.
      const core = (mappings && mappings.core) || {};
      const priceMajor = parseFloat(core.price ?? rawPrice ?? 0);
      const msrpMajor = parseFloat(core.msrp ?? msrp ?? 0);
      let price = Number.isFinite(priceMajor) ? Math.round(priceMajor * 100) : 0;
      let compareAt = Number.isFinite(msrpMajor) ? Math.round(msrpMajor * 100) : 0;
      // Athos indexes base-currency (AUD) feed prices; market shoppers get the
      // exact presentment price primed from the Storefront API instead.
      const presentment =
        typeof window.AthosAPI.getPresentmentPrice === 'function'
          ? window.AthosAPI.getPresentmentPrice(handle || '')
          : null;
      if (presentment) {
        price = presentment.price;
        compareAt = presentment.compareAt;
      }
      const onSale = compareAt > 0 && compareAt > price;
      const promo =
        window.PromoPricing && typeof window.PromoPricing.resolve === 'function'
          ? window.PromoPricing.resolve({ priceCents: price, tags: attributes.ss_tags || ss_tags })
          : null;

      const title = (name || '').split(' - ')[0];
      const colour = (name || '').split(' - ')[1] || '';
      const productHandle = handle || '';
      const productUrl = url || (productHandle ? `/products/${productHandle}` : '#');

      const images = (Array.isArray(ss_images) && ss_images) || [imageUrl || thumbnailImageUrl].filter(Boolean);
      // Shopify's auto-generated video posters (/files/preview_images/…)
      // stay in the list: they render as still slides that
      // `assets/product-card-video.js` upgrades to playing video in place.
      const media = images.map((src) => {
        let currentImageUrl = src;
        let srcset = null;
        if (currentImageUrl && currentImageUrl.includes('cdn.shopify.com')) {
          try {
            const urlObj = new URL(currentImageUrl, window.location.origin);
            urlObj.pathname = urlObj.pathname.replace(
              /_(?:\d+x\d*(?:_crop_[a-zA-Z]+)?|small|medium|large|grande|original)(\.[a-zA-Z0-9]+)$/i,
              '$1'
            );
            // Build the srcset from the clean (width-free) URL so
            // buildSrcset's appended width params never duplicate.
            urlObj.searchParams.delete('width');
            if (window.AthosAPI && typeof window.AthosAPI.buildSrcset === 'function') {
              srcset = window.AthosAPI.buildSrcset(urlObj.toString(), [400, 600, 800, 1200]);
            }
            urlObj.searchParams.set('width', '800');
            currentImageUrl = urlObj.toString();
          } catch (e) {}
        }
        return {
          image: currentImageUrl,
          alt: title,
          product_url: productUrl,
          product_title: title,
          is_image: 'true',
          srcset: srcset || null,
          sizes_attr: srcset ? '(max-width: 767px) 50vw, 25vw' : null,
        };
      });

      let parsedSizes = [];
      const rawSizes = (attributes && attributes.ss_sizes) || ss_sizes;
      if (typeof rawSizes === 'string' && rawSizes) {
        try {
          parsedSizes = JSON.parse(rawSizes.replace(/&quot;/g, '"').replace(/&amp;/g, '&'));
        } catch (_e) {
          parsedSizes = [];
        }
      } else if (Array.isArray(rawSizes)) {
        parsedSizes = rawSizes;
      }

      let sizeLabel = '';
      if (parsedSizes.length > 0) {
        const firstLabel = String(parsedSizes[0].label || '');
        if (firstLabel && firstLabel !== 'One Size' && firstLabel.includes(' ')) {
          const parts = firstLabel.split(' ');
          parts.pop();
          sizeLabel = parts.join(' ').trim();
        }
      }

      const variants = parsedSizes.map((s) => {
        const label = String(s.label || '');
        let size;
        if (label === 'One Size') {
          size = label;
        } else if (label.includes(' ')) {
          const parts = label.split(' ');
          size = (parts[parts.length - 1] || label).trim();
        } else {
          size = label;
        }
        const available = Number(s.available) > 0;
        return {
          id: String(s.id || ''),
          label,
          size,
          available,
          available_str: available ? 'true' : 'false',
          aria_disabled: available ? undefined : 'true',
          aria_label: label,
        };
      });

      const { colour_options: colourOptions, colour_overflow } = window.ProductCardBind.parseColourSwatches(
        r,
        productHandle,
        productUrl,
        colour
      );

      const rawBanners =
        attributes.mfield_custom_urgency_banners ||
        mfield_custom_urgency_banners ||
        attributes.custom_urgency_banners ||
        custom_urgency_banners ||
        attributes.urgency_banners ||
        urgency_banners;

      let parsedBanners = [];
      if (typeof rawBanners === 'string' && rawBanners) {
        try {
          const parsed = JSON.parse(rawBanners.replace(/&quot;/g, '"').replace(/&amp;/g, '&'));
          parsedBanners = Array.isArray(parsed) ? parsed : [parsed];
        } catch (_e) {
          parsedBanners = [];
        }
      } else if (Array.isArray(rawBanners)) {
        parsedBanners = rawBanners;
      } else if (typeof rawBanners === 'object' && rawBanners !== null) {
        parsedBanners = [rawBanners];
      }

      let banners = [];
      if (Array.isArray(parsedBanners)) {
        parsedBanners = parsedBanners.map((b) => {
          if (typeof b === 'string') {
            try {
              return JSON.parse(b.replace(/&quot;/g, '"').replace(/&amp;/g, '&'));
            } catch (_e) {
              return null;
            }
          }
          return b;
        });
        const nowMs = Date.now();
        banners = parsedBanners
          .filter((b) => {
            if (!b || !b.text || b.paused === true || b.show === false) return false;
            if (b.scheduledStartAt) {
              const startMs = Date.parse(b.scheduledStartAt);
              if (!isNaN(startMs) && nowMs < startMs) return false;
            }
            if (b.scheduledEndAt) {
              const endMs = Date.parse(b.scheduledEndAt);
              if (!isNaN(endMs) && nowMs > endMs) return false;
            }
            return true;
          })
          .map((b) => ({
            text: String(b.text || ''),
            style_str: `background-color: ${window.AthosAPI.safeColour(
              b.bg_color,
              '#FCFBF8'
            )}; color: ${window.AthosAPI.safeColour(b.text_color, '#050000')};`,
          }));
      }

      const badgeText = window.AthosAPI.cleanBannerText(attributes.ss_badge_text || ss_badge_text);
      if (badgeText) {
        const badgeBg = window.AthosAPI.safeColour(attributes.ss_badge_bg || ss_badge_bg, '#FCFBF8');
        const badgeColour = window.AthosAPI.safeColour(attributes.ss_badge_colour || ss_badge_colour, '#050000');
        banners.push({
          text: badgeText,
          style_str: `background-color: ${badgeBg}; color: ${badgeColour};`,
        });
      }

      const rawProductLabel = attributes.mfield_custom_product_label || mfield_custom_product_label;
      let parsedProductLabel = '';
      if (typeof rawProductLabel === 'string') {
        parsedProductLabel = window.AthosAPI.cleanBannerText(rawProductLabel);
      } else if (Array.isArray(rawProductLabel) && rawProductLabel.length > 0) {
        parsedProductLabel = typeof rawProductLabel[0] === 'string' ? rawProductLabel[0] : '';
      }

      if (typeof parsedProductLabel === 'string' && parsedProductLabel) {
        const labelParts = parsedProductLabel.split('|');
        const labelText = (labelParts[0] || '').trim();
        if (labelText) {
          const labelBg = window.AthosAPI.safeColour((labelParts[1] || '').trim(), '#FCFBF8');
          const labelColor = window.AthosAPI.safeColour((labelParts[2] || '').trim(), '#050000');
          banners.push({
            text: labelText,
            style_str: `background-color: ${labelBg}; color: ${labelColor};`,
          });
        }
      }

      const seenBannerText = {};
      banners = banners.filter(function (b) {
        const key = (b.text || '').trim().toLowerCase();
        if (!key || seenBannerText[key]) return false;
        // Text still shaped like serialised JSON or carrying entities failed
        // to decode upstream — drop it rather than render raw markup.
        if (key.startsWith('["') || key.startsWith('[{') || key.startsWith('{"') || key.indexOf('&quot;') !== -1) {
          return false;
        }
        seenBannerText[key] = true;
        return true;
      });

      return {
        id: core.parentId || r.parentId || uid || core.uid || id || '',
        handle: productHandle,
        title,
        colour,
        product_url: productUrl,
        product_title: title,
        price,
        compare_at_price: compareAt,
        swipeable: 'true',
        banners,
        formatted_price: window.AthosAPI.formatMoney(price, currencyFormat, true),
        formatted_compare_at_price: window.AthosAPI.formatMoney(compareAt, currencyFormat, true),
        on_sale: onSale,
        price_type: onSale ? 'discount' : null,
        saved_percent: onSale ? Math.floor(((compareAt - (promo ? promo.priceCents : price)) / compareAt) * 100) : 0,
        promo_percent: promo ? promo.percent : 0,
        promo_price: promo ? promo.priceCents : price,
        formatted_promo_price: promo ? window.AthosAPI.formatMoney(promo.priceCents, currencyFormat, true) : null,
        promo_message: promo
          ? window.PromoPricing.message(window.AthosAPI.formatMoney(promo.priceCents, currencyFormat, true), promo.percent) || null
          : null,
        size_label: sizeLabel,
        variants,
        colour_options: colourOptions,
        colour_overflow,
        yotpo_bottomline_raw: attributes.mfield_yotpo_preloaded_bottomline || mfield_yotpo_preloaded_bottomline || null,
        hover_image: media[1] ? media[1].image || null : null,
        media:
          media.length > 0
            ? media
            : [{ image: '', alt: title, product_url: productUrl, product_title: title, is_image: 'true' }],
      };
    };

    /**
     * Helper to flip a single slider instantly without animation
     */
    const flipSliderInstant = (slider, isProductMode) => {
      const slides = slider.querySelectorAll('.media-slider__slide');
      const activeSlide = slider.querySelector('.media-slider__slide[aria-current="true"]');
      if (!activeSlide || slides.length === 0) return false;
      const originalIndex = activeSlide.dataset.freshOriginalIndex;
      const lastIndex = String(slides.length - 1);

      let toggled = false;
      if (typeof slider.goToOriginalIndex === 'function') {
        if (isProductMode) {
          if (originalIndex !== lastIndex) {
            slider._preProductIndex = originalIndex;
            slider.goToOriginalIndex(lastIndex, { instant: true });
            toggled = true;
          }
        } else {
          const targetIndex = slider._preProductIndex !== undefined ? slider._preProductIndex : '0';
          if (originalIndex !== targetIndex) {
            slider.goToOriginalIndex(targetIndex, { instant: true });
            toggled = true;
          }
          delete slider._preProductIndex;
        }
      } else {
        // Fallback to legacy prev/next toggle
        if (isProductMode && originalIndex === '0') {
          if (typeof slider.prev === 'function') {
            slider.prev({ instant: true });
            toggled = true;
          }
        } else if (!isProductMode && originalIndex === lastIndex) {
          if (typeof slider.next === 'function') {
            slider.next({ instant: true });
            toggled = true;
          }
        }
      }

      return toggled;
    };

    /**
     * Fetch search results from AthosAPI.
     * @param {number} page
     * @returns {Promise<object>}
     */
    const fetchResults = async (page) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      // Carry the active sort.*/filter.* selections (shared with the collection
      // page via window.CollectionSortFilter) into the search request.
      const filters =
        window.CollectionSortFilter && typeof window.CollectionSortFilter.getParams === 'function'
          ? window.CollectionSortFilter.getParams()
          : undefined;
      try {
        const body = await window.AthosAPI.fetchSearch({
          query,
          resultsPerPage: state.perPage,
          page: page || 1,
          filters,
          signal: controller.signal,
        });
        await primePresentmentPrices(body);
        return body;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    /**
     * Prime the Storefront presentment-price cache for a fetched page of
     * results so `normalise` can overlay exact market prices synchronously.
     * No-op for base-currency shoppers.
     * @param {object} body - fetchSearch response body.
     * @returns {Promise<void>}
     */
    async function primePresentmentPrices(body) {
      if (!window.AthosAPI || typeof window.AthosAPI.fetchPresentmentPrices !== 'function') return;
      const products = (body && body.ok && body.products) || [];
      const handles = products
        .map(function (r) {
          return r.handle || '';
        })
        .filter(Boolean);
      if (handles.length === 0) return;
      await window.AthosAPI.fetchPresentmentPrices({ handles });
    }

    /**
     * Build a DocumentFragment of product cards from results.
     * @param {object[]} results
     * @param {string} [responseId]
     * @returns { fragment: DocumentFragment, cards: HTMLElement[] }
     */
    const renderCards = (results, responseId) => {
      const fragment = document.createDocumentFragment();
      const currentMode = root.dataset.displayMode || 'on-body';
      const cards = [];

      results.forEach((result) => {
        const data = normalise(result);
        const card = window.ProductCardBind.renderCard(template, data);
        if (card) {
          if (responseId) {
            cardDataMap.set(card, { result, responseId });
          }
          const slider = card.querySelector('media-slider');
          if (slider && currentMode === 'product') {
            window.customElements.whenDefined('media-slider').then(() => {
              flipSliderInstant(slider, true);
            });
          }
          fragment.appendChild(card);
          cards.push(card);
        }
      });
      return { fragment, cards };
    };

    /**
     * Update the results count badge.
     * @param {number} total
     */
    /**
     * Update the results count badge.
     * @param {number} total
     */
    const updateResultsCount = (total) => {
      if (!resultsCountEl) return;
      resultsCountEl.removeAttribute('hidden');
      if (total === 0) {
        resultsCountEl.textContent = resultsCountEl.dataset.i18nNoResults || 'No results found';
        if (toggleBtn) toggleBtn.setAttribute('hidden', '');
      } else {
        const tpl = resultsCountEl.dataset.template;
        resultsCountEl.textContent = tpl ? tpl.replace('[[count]]', total).split('[[terms]]').join(query) : total;
        if (toggleBtn) toggleBtn.removeAttribute('hidden');
      }
    };

    /** Update the "You've viewed X of Y" progress text. */
    const updateProgress = () => {
      if (!progressEl) return;
      const end = Math.min(state.activePage * state.perPage, state.totalResults);
      const reachedEnd = state.totalResults > 0 && state.totalPages > 0 && state.activePage >= state.totalPages;

      if (reachedEnd) {
        progressEl.textContent = i18n.allLoaded.replace('[[total]]', state.totalResults);
      } else {
        progressEl.textContent = i18n.progress.replace('[[end]]', end).replace('[[total]]', state.totalResults);
      }
    };

    /** Show/hide the Load More button based on state. */
    const updateLoadMore = () => {
      if (!loadMoreBtn) return;
      if (state.highestPage >= state.totalPages) {
        loadMoreBtn.hidden = true;
      } else {
        loadMoreBtn.hidden = false;
        loadMoreBtn.href = pageHref(state.highestPage + 1);
        loadMoreBtn.dataset.loading = 'false';
        if (loadMoreText) loadMoreText.textContent = i18n.loadMore;
        if (spinnerEl) spinnerEl.hidden = true;
      }
    };

    /** Show the skeleton grid, hide the main grid and no-results panel. */
    const showSkeleton = () => {
      if (skeletonGrid) {
        skeletonGrid.removeAttribute('hidden');
      }
      grid.setAttribute('hidden', '');
      if (noResultsEl) noResultsEl.setAttribute('hidden', '');
      if (paginationWrapper) paginationWrapper.hidden = true;
    };

    /** Hide skeleton, reveal the main grid and pagination. */
    const hideSkeleton = () => {
      grid.removeAttribute('hidden');
      if (skeletonGrid) skeletonGrid.setAttribute('hidden', '');
      if (paginationWrapper) {
        paginationWrapper.hidden = state.totalPages <= 1;
      }
    };

    /**
     * Generate the array of page indicators to render.
     * @param {number} current
     * @param {number} total
     * @returns {Array<number|string>}
     */
    const generatePageNumbers = (current, total) => {
      if (total <= 7) {
        const arr = [];
        for (let i = 1; i <= total; i++) arr.push(i);
        return arr;
      }
      if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
      if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
      return [1, '…', current - 1, current, current + 1, '…', total];
    };

    /**
     * Build the href for a given page number.
     * @param {number} page
     * @returns {string}
     */
    const pageHref = (page) => {
      const url = new URL(window.location.href);
      if (page === 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      return url.pathname + url.search;
    };

    /** Render the numbered pagination nav. */
    /** Render the numbered pagination nav. */
    const renderNav = () => {
      if (!navEl || state.totalPages <= 1) {
        if (navEl) {
          navEl.hidden = true;
          navEl.replaceChildren();
        }
        return;
      }
      navEl.hidden = false;

      const pages = generatePageNumbers(state.activePage, state.totalPages);
      const fragment = document.createDocumentFragment();

      // Previous arrow
      if (state.activePage > 1) {
        const prev = document.createElement('a');
        prev.href = pageHref(state.activePage - 1);
        prev.className = 'search-pagination__page search-pagination__arrow';
        prev.textContent = i18n.previous;
        prev.setAttribute('aria-label', i18n.previous);
        prev.dataset.page = String(state.activePage - 1);
        fragment.appendChild(prev);
      }

      // Page numbers
      pages.forEach((p) => {
        if (p === '…') {
          const span = document.createElement('span');
          span.className = 'search-pagination__ellipsis';
          span.textContent = '…';
          span.setAttribute('aria-hidden', 'true');
          fragment.appendChild(span);
        } else {
          const a = document.createElement('a');
          a.href = pageHref(p);
          a.className = 'search-pagination__page';
          a.textContent = String(p);
          a.setAttribute('aria-label', i18n.page.replace('[[page]]', p));
          a.dataset.page = String(p);
          if (p === state.activePage) {
            a.dataset.state = 'current';
            a.setAttribute('aria-current', 'page');
          } else if (p >= state.lowestPage && p <= state.highestPage) {
            a.dataset.state = 'old';
          } else {
            a.dataset.state = 'new';
          }
          fragment.appendChild(a);
        }
      });

      // Next arrow
      if (state.activePage < state.totalPages) {
        const next = document.createElement('a');
        next.href = pageHref(state.activePage + 1);
        next.className = 'search-pagination__page search-pagination__arrow';
        next.textContent = i18n.next;
        next.setAttribute('aria-label', i18n.next);
        next.dataset.page = String(state.activePage + 1);
        fragment.appendChild(next);
      }

      navEl.replaceChildren(fragment);
    };

    /** Refresh all pagination UI sub-components. */
    const refreshPaginationUI = () => {
      updateProgress();
      updateLoadMore();
      renderNav();
    };

    /**
     * Stamp the first card of a fragment with a page anchor.
     * @param {DocumentFragment} fragment
     * @param {number} page
     */
    const stampPageAnchor = (fragment, page) => {
      if (!fragment || !fragment.firstElementChild) return;
      fragment.firstElementChild.dataset.pageAnchor = String(page);
    };

    /**
     * Smooth-scroll the viewport so the first card of the page sits just below the chrome.
     * @param {number} page
     */
    const scrollToPage = (page) => {
      const anchor = grid.querySelector(`[data-page-anchor="${page}"]`);
      if (!anchor) {
        const headerEl = document.querySelector('.search-page__header');
        if (headerEl) {
          headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          headerEl.setAttribute('tabindex', '-1');
          headerEl.focus({ preventScroll: true });
        }
        return;
      }
      const headerHeight =
        parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      const offset = headerHeight + 20;
      const top = anchor.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      anchor.setAttribute('tabindex', '-1');
      anchor.focus({ preventScroll: true });
    };

    /**
     * Fetch and append the next page.
     * @returns {Promise<boolean>}
     */
    /**
     * Fetch and append the next page.
     * @returns {Promise<boolean>}
     */
    const loadAppendNext = async () => {
      if (state.isFetching || state.highestPage >= state.totalPages) return false;
      state.isFetching = true;
      try {
        const nextPage = state.highestPage + 1;
        const body = await fetchResults(nextPage);
        if (body && body.aborted) return false;
        if (!body || body.error) {
          revealFallback();
          return false;
        }
        if (body.products.length === 0) return false;

        state.highestPage = nextPage;
        state.activePage = nextPage;
        state.displayedCount += body.products.length;

        const responseId = body.responseId;
        const { fragment, cards } = renderCards(body.products, responseId);
        stampPageAnchor(fragment, nextPage);
        grid.appendChild(fragment);
        lazyLoadCards(grid);

        if (responseId && body.rawResults) {
          if (window.AthosBeacon) window.AthosBeacon.sendSearchRender(responseId);
          observeImpressions(responseId, body.rawResults, cards);
        }

        updateURL(nextPage, false);
        refreshPaginationUI();
        return true;
      } catch (err) {
        if (window.console && console.warn) console.warn('search: append-next failed', err && err.message);
        return false;
      } finally {
        state.isFetching = false;
      }
    };

    /**
     * Fetch and prepend the page.
     * @returns {Promise<boolean>}
     */
    const loadPrependPrevious = async () => {
      if (state.isFetching || state.lowestPage <= 1) return false;
      state.isFetching = true;
      try {
        const prevPage = state.lowestPage - 1;
        const scrollBefore = window.scrollY;

        const body = await fetchResults(prevPage);
        if (body && body.aborted) return false;
        if (!body || body.error) {
          revealFallback();
          return false;
        }
        if (body.products.length === 0) return false;

        state.lowestPage = prevPage;
        state.displayedCount += body.products.length;

        const responseId = body.responseId;
        const { fragment, cards } = renderCards(body.products, responseId);
        stampPageAnchor(fragment, prevPage);

        const heightBefore = grid.scrollHeight;
        grid.insertBefore(fragment, grid.firstChild);
        lazyLoadCards(grid);

        if (responseId && body.rawResults) {
          if (window.AthosBeacon) window.AthosBeacon.sendSearchRender(responseId);
          observeImpressions(responseId, body.rawResults, cards);
        }

        const heightAfter = grid.scrollHeight;
        const heightDelta = heightAfter - heightBefore;
        window.scrollTo({ top: scrollBefore + heightDelta, behavior: 'instant' });

        updateURL(state.activePage, false);
        refreshPaginationUI();
        return true;
      } catch (err) {
        if (window.console && console.warn) console.warn('search: prepend-previous failed', err && err.message);
        return false;
      } finally {
        state.isFetching = false;
      }
    };

    /** Read the page number from the current URL. Defaults to 1. */
    const getPageFromURL = () => {
      const params = new URLSearchParams(window.location.search);
      const p = parseInt(params.get('page'), 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    };

    /** Update browser URL without a reload. */
    const updateURL = (page, push) => {
      const url = pageHref(page);
      if (push) {
        window.history.pushState({ ssPage: page }, '', url);
      } else {
        window.history.replaceState({ ssPage: page }, '', url);
      }
    };

    /**
     * Fetch a single page and replace the entire grid.
     * @param {number} page
     * @param {object} [opts]
     */
    const loadPage = async (page, opts = {}) => {
      if (state.isFetching) {
        state.pendingLoad = { page: page, opts: opts };
        return;
      }
      state.isFetching = true;

      if (opts.showSkeleton) showSkeleton();

      try {
        const body = await fetchResults(page);

        if (body && body.aborted) {
          return;
        }

        if (!body || body.error) {
          revealFallback();
          return;
        }

        const safeRedirect = window.AthosAPI.sanitiseRedirect(body.redirect);
        if (safeRedirect) {
          if (window.AthosBeacon && body.responseId)
            window.AthosBeacon.sendSearchRedirect(body.responseId, safeRedirect);
          window.location.replace(safeRedirect);
          return;
        }

        // Feed facets/sort back to the shared filter UI (populates the drawers,
        // sort select and active pills). Runs even for zero results so filters
        // remain adjustable.
        if (window.CollectionSortFilter && typeof window.CollectionSortFilter.handleResponse === 'function') {
          window.CollectionSortFilter.handleResponse({
            pagination: { totalResults: body.totalResults || 0 },
            sorting: body.sorting,
            facets: body.facets,
          });
        }

        if (body.products.length === 0) {
          if (page > 1) {
            loadPage(1, { showSkeleton: true, scroll: true, push: false });
            return;
          }
          if (skeletonGrid) skeletonGrid.setAttribute('hidden', '');
          if (noResultsEl) {
            noResultsEl.removeAttribute('hidden');
            if (noResultsMessageEl) {
              const msgTpl = noResultsEl.dataset.i18nNoResults || 'No results found for "[[terms]]"';
              noResultsMessageEl.textContent = msgTpl.split('[[terms]]').join(query);
            }
          }
          updateResultsCount(0);
          if (paginationWrapper) paginationWrapper.hidden = true;
          return;
        }

        if (noResultsEl) noResultsEl.setAttribute('hidden', '');

        state.totalPages = body.totalPages || 1;
        state.totalResults = body.totalResults || 0;
        state.perPage = body.perPage || 24;
        state.activePage = page;
        state.lowestPage = page;
        state.highestPage = page;
        state.displayedCount = body.products.length;

        updateResultsCount(state.totalResults);

        const responseId = body.responseId;
        if (window.AthosBeacon && responseId) {
          window.AthosBeacon.sendSearchRender(responseId);
        }

        const { fragment, cards } = renderCards(body.products, responseId);
        stampPageAnchor(fragment, page);
        disconnectObservers();
        grid.replaceChildren(fragment);
        hideSkeleton();
        if (initialGridRender) {
          initialGridRender = false;
          prioritiseLeadImages(cards, 2);
          grid.setAttribute('data-cards-resolving', '');
          setTimeout(() => {
            grid.removeAttribute('data-cards-resolving');
          }, 700);
        }
        lazyLoadCards(grid);

        if (responseId && body.rawResults) {
          observeImpressions(responseId, body.rawResults, cards);
        }

        refreshPaginationUI();

        if (opts.push) {
          updateURL(page, true);
        } else {
          updateURL(page, false);
        }

        if (opts.scroll) {
          const headerEl = document.querySelector('.search-page__header');
          if (headerEl) headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Save search query to localStorage history using window.AthosAPI
        if (window.AthosAPI && typeof window.AthosAPI.saveToHistory === 'function') {
          window.AthosAPI.saveToHistory(query);
        }
      } catch (err) {
        revealFallback();
        if (window.console && console.warn) {
          console.warn('search: search fetch failed', err && err.message);
        }
      } finally {
        state.isFetching = false;
        if (state.pendingLoad) {
          const pending = state.pendingLoad;
          state.pendingLoad = null;
          if (pending.page !== state.activePage) loadPage(pending.page, pending.opts);
        }
      }
    };

    /** Handle "Load More" click. */
    const handleLoadMore = async (e) => {
      if (e) e.preventDefault();
      if (state.isFetching || state.highestPage >= state.totalPages) return;

      if (loadMoreBtn) loadMoreBtn.dataset.loading = 'true';
      if (loadMoreText) loadMoreText.hidden = true;
      if (spinnerEl) spinnerEl.hidden = false;

      try {
        await loadAppendNext();
      } finally {
        if (loadMoreBtn) loadMoreBtn.dataset.loading = 'false';
        if (loadMoreText) loadMoreText.hidden = false;
        if (spinnerEl) spinnerEl.hidden = true;
      }
    };

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', handleLoadMore);
    }

    if (navEl) {
      navEl.addEventListener('click', async (e) => {
        const link = e.target.closest('[data-page]');
        if (!link) return;
        e.preventDefault();
        const page = parseInt(link.dataset.page, 10);
        if (!Number.isFinite(page) || page === state.activePage) return;

        if (page >= state.lowestPage && page <= state.highestPage) {
          state.activePage = page;
          updateURL(page, true);
          refreshPaginationUI();
          scrollToPage(page);
          return;
        }

        if (state.isFetching) return;
        navEl.setAttribute('aria-busy', 'true');
        navEl.style.pointerEvents = 'none';
        try {
          if (page === state.highestPage + 1) {
            const ok = await loadAppendNext();
            if (ok) {
              state.activePage = page;
              updateURL(page, true);
              refreshPaginationUI();
              scrollToPage(page);
            }
          } else if (page === state.lowestPage - 1) {
            const ok = await loadPrependPrevious();
            if (ok) {
              state.activePage = page;
              updateURL(page, true);
              refreshPaginationUI();
              scrollToPage(page);
            }
          } else {
            loadPage(page, { showSkeleton: true, scroll: true, push: true });
            return;
          }
        } finally {
          navEl.removeAttribute('aria-busy');
          navEl.style.pointerEvents = '';
        }
      });
    }

    if (grid) {
      grid.addEventListener('click', (e) => {
        if (!e.target.closest('a[href]')) return;
        const card = e.target.closest('[data-js="product-card"]');
        if (!card || !window.AthosBeacon) return;
        const data = cardDataMap.get(card);
        if (data && data.responseId && data.result) {
          window.AthosBeacon.sendSearchClickthrough(data.responseId, data.result);
        }
      });

      grid.addEventListener('quick-add:added', (e) => {
        const card = e.target.closest('[data-js="product-card"]');
        if (!card || !window.AthosBeacon) return;
        const data = cardDataMap.get(card);
        if (data && data.responseId && data.result) {
          const addedItem = e.detail && e.detail.item ? e.detail.item : null;
          window.AthosBeacon.sendSearchAddtocart(data.responseId, data.result, addedItem);
        }
      });
    }

    /**
     * Fetch recommendations (query suggestions) from AthosAPI and render them.
     *
     * @returns {Promise<void>}
     */
    const loadRecommendations = async () => {
      if (!query || !recommendationsEl || !recommendationsListEl) return;
      if (!window.AthosAPI || typeof window.AthosAPI.fetchSuggest !== 'function') return;

      try {
        const result = await window.AthosAPI.fetchSuggest({ query, limit: 5, suggestionsOnly: true });
        if (result && result.ok && Array.isArray(result.suggestions) && result.suggestions.length > 0) {
          const fragment = document.createDocumentFragment();
          result.suggestions.forEach((term) => {
            const a = document.createElement('a');
            a.href = `${basePath}?q=${encodeURIComponent(term)}`;
            a.className = 'search-page__recommendations-link';
            a.textContent = term;
            fragment.appendChild(a);
          });
          recommendationsListEl.replaceChildren(fragment);
          recommendationsEl.removeAttribute('hidden');
        } else {
          recommendationsEl.setAttribute('hidden', '');
        }
      } catch (err) {
        if (window.console && console.warn) console.warn('search: fetchSuggest failed', err && err.message);
      }
    };

    window.addEventListener('popstate', (e) => {
      const page = e.state && e.state.ssPage ? e.state.ssPage : getPageFromURL();
      loadPage(page, { showSkeleton: true, scroll: true, push: false });
    });

    // When a sort option or filter changes, CollectionSortFilter flushes the
    // URL then calls this to re-fetch page 1 with the new params.
    if (window.CollectionSortFilter && typeof window.CollectionSortFilter.register === 'function') {
      window.CollectionSortFilter.register(() => {
        loadPage(1, { showSkeleton: true, scroll: false, push: false });
      });
    }

    // Count-only preview for the staged mobile filter drawer: total for the
    // currently staged params, without rendering. Aborts the prior preview.
    let previewController = null;
    if (window.CollectionSortFilter && typeof window.CollectionSortFilter.registerPreview === 'function') {
      window.CollectionSortFilter.registerPreview(async () => {
        if (previewController) previewController.abort();
        previewController = new AbortController();
        const filters = window.CollectionSortFilter.getParams();
        const res = await window.AthosAPI.fetchSearch({
          query,
          resultsPerPage: 1,
          page: 1,
          filters,
          signal: previewController.signal,
        });
        // fetchSearch returns a flattened shape (totalResults at the top level).
        if (res && res.ok && typeof res.totalResults === 'number') {
          return res.totalResults;
        }
        return null;
      });
    }

    const initialPage = getPageFromURL();
    if (!(initialPage === 1 && !new URLSearchParams(window.location.search).has('page'))) {
      window.history.replaceState({ ssPage: initialPage }, '', window.location.href);
    }
    loadPage(initialPage, { showSkeleton: false, scroll: false, push: false });
    loadRecommendations();
  })();

  (() => {
    const toggleBtn = document.querySelector('[data-js="product-display-toggle"]');
    if (!toggleBtn) return;

    const container = document.querySelector('[data-js="search"]');

    if (container) {
      container.dataset.displayMode = toggleBtn.dataset.active;
    }

    const performToggleUpdate = (newState) => {
      toggleBtn.dataset.active = newState;
      toggleBtn.setAttribute('aria-checked', newState === 'product' ? 'true' : 'false');

      if (container) {
        container.dataset.displayMode = newState;
      }

      // Instantly flip the sliders
      const mediaSliders = document.querySelectorAll('media-slider');
      mediaSliders.forEach((slider) => {
        const slides = slider.querySelectorAll('.media-slider__slide');
        const activeSlide = slider.querySelector('.media-slider__slide[aria-current="true"]');
        if (!activeSlide || slides.length === 0) return;
        const originalIndex = activeSlide.dataset.freshOriginalIndex;
        const lastIndex = String(slides.length - 1);

        if (typeof slider.goToOriginalIndex === 'function') {
          if (newState === 'product') {
            if (originalIndex !== lastIndex) {
              slider._preProductIndex = originalIndex;
              slider.goToOriginalIndex(lastIndex, { instant: true });
            }
          } else {
            const targetIndex = slider._preProductIndex !== undefined ? slider._preProductIndex : '0';
            if (originalIndex !== targetIndex) {
              slider.goToOriginalIndex(targetIndex, { instant: true });
            }
            delete slider._preProductIndex;
          }
        } else {
          // Fallback to legacy prev/next toggle
          if (newState === 'product' && originalIndex === '0') {
            if (typeof slider.prev === 'function') {
              slider.prev({ instant: true });
            }
          } else if (newState !== 'product' && originalIndex === lastIndex) {
            if (typeof slider.next === 'function') {
              slider.next({ instant: true });
            }
          }
        }
      });

      // Emit event so the product grid/cards can respond to the display mode change
      window.dispatchEvent(new CustomEvent('collection:display-mode', { detail: { mode: newState } }));
    };

    toggleBtn.addEventListener('click', () => {
      const isProduct = toggleBtn.dataset.active === 'product';
      const newState = isProduct ? 'on-body' : 'product';

      if (document.startViewTransition) {
        document.startViewTransition(() => performToggleUpdate(newState));
      } else {
        performToggleUpdate(newState);
      }
    });
  })();
